/**
 * Badge Manager Utility
 * 
 * This utility module provides functions for managing badges using the RPC functions
 * that bypass row-level security policies for safer badge operations.
 */
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { BadgeEarnedToast } from "@/components/ui/BadgeEarnedToast";

/**
 * Get a badge by its name
 * @param badgeName The name of the badge to retrieve
 * @returns The badge object or null if not found
 */
export async function getBadgeByName(badgeName: string) {
  const { data, error } = await supabase.rpc(
    'get_badge_by_name',
    { badge_name: badgeName }
  );
  
  if (error) {
    console.error(`Error getting badge ${badgeName}:`, error);
    return null;
  }
  
  return data;
}

/**
 * Ensure a badge exists, creating it if necessary
 * @param badgeName The name of the badge
 * @param badgeDescription The description of the badge
 * @param badgeIcon The icon (emoji) for the badge
 * @param badgeColor The CSS color class for the badge
 * @returns The badge ID or null if an error occurred
 */
export async function ensureBadgeExists(
  badgeName: string,
  badgeDescription: string,
  badgeIcon: string,
  badgeColor: string
) {
  const { data: badgeId, error } = await supabase.rpc(
    'ensure_badge_exists',
    {
      badge_name: badgeName,
      badge_description: badgeDescription,
      badge_icon: badgeIcon,
      badge_color: badgeColor
    }
  );
  
  if (error) {
    console.error(`Error ensuring badge "${badgeName}" exists:`, error);
    return null;
  }
  
  return badgeId;
}

/**
 * Check if a user has a specific badge
 * @param userId The user ID to check
 * @param badgeId The badge ID to check for
 * @returns Boolean indicating if the user has the badge
 */
export async function userHasBadge(userId: string, badgeId: string) {
  const { data, error } = await supabase.rpc(
    'user_has_badge',
    {
      user_id_param: userId,
      badge_id_param: badgeId
    }
  );
  
  if (error) {
    console.error(`Error checking if user ${userId} has badge ${badgeId}:`, error);
    return false;
  }
  
  return !!data;
}

/**
 * Award a badge to a user
 * @param userId The user ID to award the badge to
 * @param badgeId The badge ID to award
 * @param progressCurrent Current progress (default: 100)
 * @param progressTotal Total progress needed (default: 100)
 * @returns Boolean indicating success
 */
export async function awardBadgeToUser(
  userId: string,
  badgeId: string,
  progressCurrent = 100,
  progressTotal = 100
) {
  console.log(`Attempting to award badge ${badgeId} to user ${userId} with progress ${progressCurrent}/${progressTotal}`);
  
  try {
    const { data: awarded, error } = await supabase.rpc(
      'award_badge_to_user',
      {
        user_id_param: userId,
        badge_id_param: badgeId,
        progress_current_param: progressCurrent,
        progress_total_param: progressTotal
      }
    );
    
    if (error) {
      console.error(`Error awarding badge ${badgeId} to user ${userId}:`, error);
      console.error(`[BADGE SYSTEM DEBUG] Award badge error details:`, {
        userId,
        badgeId,
        progressCurrent,
        progressTotal,
        error: {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        }
      });
      return false;
    }
    
    return !!awarded;
  } catch (err) {
    console.error(`Exception in awardBadgeToUser for badge ${badgeId} and user ${userId}:`, err);
    return false;
  }
}

/**
 * Award a badge to a user by badge name
 * This function handles the entire process: ensuring the badge exists and awarding it
 * 
 * @param userId The user ID to award the badge to
 * @param badgeName The name of the badge to award
 * @param badgeDescription The description of the badge
 * @param badgeIcon The icon (emoji) for the badge
 * @param badgeColor The CSS color class for the badge
 * @param progressCurrent Current progress (default: 100)
 * @param progressTotal Total progress needed (default: 100)
 * @param shouldNotify Whether to show a toast notification (default: true)
 * @returns Boolean indicating success
 */
export async function awardBadgeByName(
  userId: string,
  badgeName: string,
  badgeDescription: string,
  badgeIcon: string,
  badgeColor: string,
  progressCurrent = 100,
  progressTotal = 100,
  shouldNotify = true
) {
  console.log(`Awarding "${badgeName}" badge to user ${userId}`);
  
  try {
    // 1. Ensure the badge exists
    const badgeId = await ensureBadgeExists(
      badgeName,
      badgeDescription,
      badgeIcon,
      badgeColor
    );
    
    if (!badgeId) {
      console.error(`Failed to ensure badge "${badgeName}" exists`);
      return false;
    }
    
    // 2. Award the badge to the user
    const awarded = await awardBadgeToUser(
      userId,
      badgeId,
      progressCurrent,
      progressTotal
    );
    
    // 3. If the badge was just earned (progress >= total), create notification and show toast
    if (awarded && progressCurrent >= progressTotal) {
      // Get existing badge status to check if it was just earned
      const { data: badgeStatus } = await supabase
        .from('user_badges')
        .select('earned_at')
        .eq('user_id', userId)
        .eq('badge_id', badgeId)
        .single();
      
      // If earned_at exists and is recent (within last 5 seconds), consider it newly earned
      const wasJustEarned = badgeStatus?.earned_at && 
        new Date(badgeStatus.earned_at).getTime() > Date.now() - 5000;
      
      // Create a notification in the database for the newly earned badge
      if (wasJustEarned) {
        try {
          await supabase
            .from('notifications')
            .insert({
              id: crypto.randomUUID(),
              user_id: userId,
              type: 'achievement',
              content: `Congratulations! You earned the "${badgeName}" badge!`,
              created_at: new Date().toISOString(),
              is_read: false,
              entity_type: 'badge',
              entity_id: badgeId
            });
          
          console.log(`Created badge notification for user ${userId}, badge ${badgeName}`);
        } catch (notificationError) {
          console.error(`Error creating badge notification:`, notificationError);
          // Continue even if notification creation fails
        }
      }
      
      // Show toast notification if requested
      if (shouldNotify) {
        showBadgeEarnedToast(badgeName);
      }
    }
    
    return true;
  } catch (err) {
    console.error(`Exception awarding badge "${badgeName}" to user ${userId}:`, err);
    return false;
  }
}

// Predefined badges with their details
export const BADGES = {
  NEW_USER: {
    name: "New User",
    description: "Awarded to new members when they join Vibebops!",
    icon: "🎉",
    color: "bg-emerald-500"
  },
  FIRST_POST: {
    name: "First Post",
    description: "Awarded for creating your first post",
    icon: "📝",
    color: "bg-blue-500"
  },
  SOCIAL_BUTTERFLY: {
    name: "Social Butterfly",
    description: "For engaging with 10+ other users' posts",
    icon: "🦋",
    color: "bg-purple-500"
  },
  TOP_COMMENTER: {
    name: "Top Commenter",
    description: "For leaving 25+ comments",
    icon: "💬",
    color: "bg-indigo-500"
  },
  CONNECTOR: {
    name: "Connector",
    description: "For gaining 10+ followers or following 20+ users",
    icon: "🔗",
    color: "bg-cyan-500"
  },
  FRIENDLY_FACE: {
    name: "Friendly Face",
    description: "For receiving 5+ positive reactions (likes, upvotes)",
    icon: "😊",
    color: "bg-yellow-500"
  },
  RISING_STAR: {
    name: "Rising Star",
    description: "For a post that gets 10+ likes or shares",
    icon: "⭐",
    color: "bg-yellow-400"
  },
  CREATIVE_SPARK: {
    name: "Creative Spark",
    description: "For uploading 5 unique posts",
    icon: "✨",
    color: "bg-violet-500"
  },
  VIRAL_MOMENT: {
    name: "Viral Moment",
    description: "For a post that receives 100+ likes, upvotes or high engagement",
    icon: "🚀",
    color: "bg-blue-600"
  }
};

// Helper functions for common badge awards

/**
 * Award the "New User" badge to a user
 */
export async function awardNewUserBadge(userId: string) {
  const badge = BADGES.NEW_USER;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color
  );
}

/**
 * Award the "First Post" badge to a user
 */
export async function awardFirstPostBadge(userId: string) {
  const badge = BADGES.FIRST_POST;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color
  );
}

/**
 * Award or update progress toward the "Social Butterfly" badge
 */
export async function updateSocialButterflyBadge(userId: string, interactionsCount: number, totalNeeded = 10) {
  const badge = BADGES.SOCIAL_BUTTERFLY;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    interactionsCount,
    totalNeeded
  );
}

/**
 * Award or update progress toward the "Top Commenter" badge
 */
export async function updateTopCommenterBadge(userId: string, commentsCount: number, totalNeeded = 25) {
  const badge = BADGES.TOP_COMMENTER;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    commentsCount,
    totalNeeded
  );
}

/**
 * Award or update progress toward the "Connector" badge
 */
export async function updateConnectorBadge(userId: string, connectionCount: number, totalNeeded = 10) {
  const badge = BADGES.CONNECTOR;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    connectionCount,
    totalNeeded
  );
}

/**
 * Award or update progress toward the "Friendly Face" badge
 */
export async function updateFriendlyFaceBadge(userId: string, reactionsCount: number, totalNeeded = 5) {
  const badge = BADGES.FRIENDLY_FACE;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    reactionsCount,
    totalNeeded
  );
}

/**
 * Award or update progress toward the "Rising Star" badge
 */
export async function updateRisingStarBadge(userId: string, likesCount: number, totalNeeded = 10) {
  const badge = BADGES.RISING_STAR;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    likesCount,
    totalNeeded
  );
}

/**
 * Award or update progress toward the "Creative Spark" badge
 */
export async function updateCreativeSparkBadge(userId: string, postsCount: number, totalNeeded = 5) {
  const badge = BADGES.CREATIVE_SPARK;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    postsCount,
    totalNeeded
  );
}

/**
 * Award or update progress toward the "Viral Moment" badge
 */
export async function updateViralMomentBadge(userId: string, engagementCount: number, totalNeeded = 100) {
  const badge = BADGES.VIRAL_MOMENT;
  return awardBadgeByName(
    userId,
    badge.name,
    badge.description,
    badge.icon,
    badge.color,
    engagementCount,
    totalNeeded
  );
}

/**
 * Ensure all system badges exist in the database
 * This creates all the standard badges used in the application
 * @returns true if successful, false otherwise
 */
export async function ensureAllBadgesExist() {
  console.log('Ensuring all badges exist in the database');
  
  try {
    const badges = Object.values(BADGES);
    const results = await Promise.all(
      badges.map(badge => 
        ensureBadgeExists(
          badge.name,
          badge.description,
          badge.icon,
          badge.color
        )
      )
    );
    
    // Check if any badge failed to be created
    const success = results.every(result => result !== null);
    
    if (success) {
      console.log('Successfully ensured all badges exist');
    } else {
      console.error('Failed to ensure some badges exist');
    }
    
    return success;
  } catch (err) {
    console.error('Exception ensuring all badges exist:', err);
    return false;
  }
}

/**
 * Trigger the confetti effect for badge earning celebrations
 */
export function triggerBadgeConfetti() {
  import('canvas-confetti').then((confetti) => {
    const duration = 2000;
    const end = Date.now() + duration;
    
    // Gold/medal colors for badge confetti
    const colors = ['#FFD700', '#FFA500', '#FFC0CB', '#C0C0C0'];
    
    (function frame() {
      confetti.default({
        particleCount: 40,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: colors,
      });
      
      confetti.default({
        particleCount: 40,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: colors,
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  });
}

/**
 * Show a toast notification for a newly earned badge
 * @param badgeName The name of the badge
 */
export function showBadgeEarnedToast(badgeName: string) {
  toast({
    title: "🎉 Badge Earned!",
    description: `You've earned the "${badgeName}" badge!`,
    variant: "default",
    duration: 6000, // Show for 6 seconds
  });
  
  // Trigger confetti effect
  triggerBadgeConfetti();
}

/**
 * Check for badge-related notifications and show a toast if there are new ones
 * @param notifications Array of notifications to check
 */
export function checkForBadgeNotifications(notifications: any[]) {
  if (!notifications || !notifications.length) return;
  
  // Look for unread achievement notifications related to badges
  const badgeNotification = notifications.find(
    notification => 
      notification.type === 'achievement' && 
      notification.entity_type === 'badge' && 
      !notification.is_read
  );
  
  if (badgeNotification) {
    // Extract badge name from notification content (e.g., "Congratulations! You earned the "First Post" badge!")
    const badgeNameMatch = badgeNotification.content.match(/earned the "([^"]+)"/i);
    if (badgeNameMatch && badgeNameMatch[1]) {
      const badgeName = badgeNameMatch[1];
      
      // Show toast with confetti
      showBadgeEarnedToast(badgeName);
    } else {
      // Fallback if we can't extract the badge name
      showBadgeEarnedToast("New Badge");
    }
  }
} 